type CompressionOptions = {
    maxWidth?: number;
    maxHeight?: number;
    maxBytes?: number;
    outputType?: string;
    initialQuality?: number;
    minQuality?: number;
};

const readBlobAsBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
            reject(new Error('Could not read blob as base64'));
            return;
        }
        resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read blob as base64'));
    reader.readAsDataURL(blob);
});

const loadImage = (file: File): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
    };
    image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Could not load image file'));
    };

    image.src = objectUrl;
});

const toBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> =>
    new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Could not create compressed blob'));
                return;
            }
            resolve(blob);
        }, type, quality);
    });

export const compressImageFileToBase64 = async (
    file: File,
    options: CompressionOptions = {}
): Promise<string> => {
    const {
        maxWidth = 1600,
        maxHeight = 1600,
        maxBytes = 900_000,
        outputType = 'image/webp',
        initialQuality = 0.85,
        minQuality = 0.5,
    } = options;

    const image = await loadImage(file);

    const scale = Math.min(1, maxWidth / image.width, maxHeight / image.height);
    let targetWidth = Math.max(1, Math.round(image.width * scale));
    let targetHeight = Math.max(1, Math.round(image.height * scale));

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Could not create canvas context');
    }

    let blob: Blob | null = null;

    for (let iteration = 0; iteration < 8; iteration += 1) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        context.clearRect(0, 0, targetWidth, targetHeight);
        context.drawImage(image, 0, 0, targetWidth, targetHeight);

        let quality = initialQuality;
        blob = await toBlob(canvas, outputType, quality);

        while (blob.size > maxBytes && quality > minQuality) {
            quality = Math.max(minQuality, Number((quality - 0.08).toFixed(2)));
            blob = await toBlob(canvas, outputType, quality);
        }

        if (blob.size <= maxBytes) {
            return readBlobAsBase64(blob);
        }

        if (targetWidth <= 320 || targetHeight <= 320) {
            break;
        }

        targetWidth = Math.max(320, Math.round(targetWidth * 0.85));
        targetHeight = Math.max(320, Math.round(targetHeight * 0.85));
    }

    if (!blob) {
        throw new Error('Could not compress image');
    }

    throw new Error(`Image could not be compressed below ${maxBytes} bytes`);
};
